import * as i0 from '@angular/core';
import { InjectionToken, inject, Injectable, APP_INITIALIZER } from '@angular/core';
import { Subject, interval, tap, filter, map, concat, from } from 'rxjs';

class MessageMetaData {
    /**
     * Constructor for this class.
     *
     * @param _topic Original topic associated to the message sent through the events bus.
     * @param [_data] Optional: Additional data sent with the message.
     */
    constructor(
    /**
     * Original topic associated to the message.
     * @private
     */
    _topic, 
    /**
     * Data associated to message. It's optional.
     * @private
     */
    _data) {
        this._topic = _topic;
        this._data = _data;
        this._id = this.uuid();
        this._timestamp = new Date().getTime();
    }
    /**
     * Gets unique identifier of the message sent through the events bus.
     */
    get id() {
        return this._id;
    }
    /**
     * Original topic associated to the message.
     */
    get topic() {
        return this._topic;
    }
    /**
     * Data associated to message. It's optional.
     */
    get data() {
        return this._data;
    }
    /**
     * Gets the time in milliseconds in which the message was generated.
     */
    get timestamp() {
        return this._timestamp;
    }
    /**
     * Generates UUID version 4. The solution above uses Math.random() for brevity, however Math.random() is not
     * guaranteed to be a high-quality RNG.
     *
     * @return UUID version 4.
     */
    uuid() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }
}

const RXAP_PUB_SUB_CACHE_SIZE = new InjectionToken('rxap/pub-sub/max-cache-size', {
    providedIn: 'root',
    factory: () => 100,
});
const RXAP_PUB_SUB_GARBAGE_COLLECTOR_INTERVAL = new InjectionToken('rxap/pub-sub/garbage-collector-interval', {
    providedIn: 'root',
    factory: () => 1000 * 60, // 1 minutes
});
const RXAP_PUB_SUB_DISABLE_CACHE = new InjectionToken('rxap/pub-sub/disable-cache', {
    providedIn: 'root',
    factory: () => false,
});
const RXAP_PUB_SUB_DISABLE_GARBAGE_COLLECTOR = new InjectionToken('rxap/pub-sub/disable-garbage-collector', {
    providedIn: 'root',
    factory: () => false,
});

class PubSubService {
    constructor() {
        /**
         * Main observable to multicast to all observers.
         */
        this.messageBus = new Subject();
        this.cache = [];
        this.maxCacheSize = inject(RXAP_PUB_SUB_CACHE_SIZE);
        /**
         * Topic message separator.
         */
        this.separator = '.';
        this.garbageCollectorInterval = inject(RXAP_PUB_SUB_GARBAGE_COLLECTOR_INTERVAL);
        this.disableCache = inject(RXAP_PUB_SUB_DISABLE_CACHE);
        this.disableGarbageCollector = inject(RXAP_PUB_SUB_DISABLE_GARBAGE_COLLECTOR);
        this.garbageCollectorSubscription = null;
        this.garbageCollectorInitialized = false;
    }
    get cacheSize() {
        return this.cache.length;
    }
    setMaxCacheSize(size) {
        this.maxCacheSize = size;
    }
    startGarbageCollector() {
        this.garbageCollectorInitialized = true;
        if (this.garbageCollectorInitialized) {
            console.warn('Garbage collector is already initialized');
            return;
        }
        if (this.disableGarbageCollector || this.disableCache) {
            console.warn('Garbage collector is disabled');
            return;
        }
        if (this.garbageCollectorSubscription) {
            this.garbageCollectorSubscription.unsubscribe();
            this.garbageCollectorSubscription = null;
        }
        this.garbageCollectorSubscription = interval(this.garbageCollectorInterval).pipe(tap(() => this.runGarbageCollector())).subscribe();
    }
    runGarbageCollector() {
        const now = Date.now();
        this.cache.forEach((message, index) => {
            if (message.retention) {
                const diff = now - message.metadata.timestamp;
                if (diff > message.retention) {
                    this.cache.splice(index, 1);
                }
            }
        });
    }
    stopGarbageCollector() {
        this.garbageCollectorSubscription?.unsubscribe();
    }
    ngOnDestroy() {
        this.stopGarbageCollector();
    }
    /**
     * Validates topic matching.
     *
     * @param topic Topic to identify the message/event.
     * @param wildcard Wildcard received from on method.
     *
     * @return true if topic matches, false otherwise.
     */
    topicMatch(topic, wildcard) {
        const w = '*';
        const ww = '**';
        const partMatch = (wl, k) => {
            return wl === w || wl === k;
        };
        const sep = this.separator;
        const kArr = topic.split(sep);
        const wArr = wildcard.split(sep);
        const kLen = kArr.length;
        const wLen = wArr.length;
        const max = Math.max(kLen, wLen);
        for (let i = 0; i < max; i++) {
            const cK = kArr[i];
            const cW = wArr[i];
            if (cW === ww && typeof cK !== 'undefined') {
                return true;
            }
            if (!partMatch(cW, cK)) {
                return false;
            }
        }
        return true;
    }
    /**
     * @param  topic Topic to identify the message.
     * @param  [data] Optional: Additional data sent with the message.
     * @param  [retention] Optional: Retention time in milliseconds.
     * @throws {Error} topic parameter must be a string and must not be empty.
     */
    publish(topic, data, retention) {
        if (!this.garbageCollectorInitialized) {
            console.error('Garbage collector is not initialized. Ensure the ProvidePubSub function is called in the app config object');
            this.startGarbageCollector();
        }
        if (!topic.trim().length) {
            throw new Error('topic parameter must be a string and must not be empty');
        }
        const metadata = new MessageMetaData(topic, data);
        this.messageBus.next({ topic, metadata });
        if (!this.disableCache && (retention === undefined || retention > 0)) {
            if (this.cache.length >= this.maxCacheSize) {
                this.cache.shift();
            }
            this.cache.push({ topic, metadata, retention });
        }
    }
    /**
     * @param topic Topic to identify the message.
     * @param replayCount Number of messages to replay.
     * @return Observable you can subscribe to listen messages.
     */
    subscribe(topic, replayCount) {
        if (!topic.trim().length) {
            throw new Error('topic parameter must be a string and must not be empty');
        }
        if (!this.garbageCollectorInitialized) {
            console.error('Garbage collector is not initialized. Ensure the ProvidePubSub function is called in the app config object');
            this.startGarbageCollector();
        }
        const fromCache = replayCount && replayCount > 0 ? this.getFromCache(topic, replayCount ?? 0) : [];
        const fromBus = this.messageBus.asObservable().pipe(filter((event) => this.topicMatch(event.topic, topic)), map((event) => event.metadata));
        return fromCache.length ? concat(from(fromCache), fromBus) : fromBus;
    }
    /**
     * @param topic Topic to identify the message.
     * @param limit Maximum number of messages to return.
     */
    getFromCache(topic, limit = 0) {
        if (!this.garbageCollectorInitialized) {
            console.error('Garbage collector is not initialized. Ensure the ProvidePubSub function is called in the app config object');
            this.startGarbageCollector();
        }
        const messages = [];
        for (let i = this.cache.length - 1; i >= 0; i--) {
            const message = this.cache[i];
            if (this.topicMatch(message.topic, topic)) {
                messages.push(message.metadata);
                if (limit && messages.length >= limit) {
                    break;
                }
            }
        }
        return messages;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: PubSubService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: PubSubService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: PubSubService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

function ProvidePubSub(...providers) {
    return [
        {
            provide: APP_INITIALIZER,
            useFactory: (service) => () => service.startGarbageCollector(),
            deps: [PubSubService],
            multi: true,
        },
        ...providers,
    ];
}
function withDisableCache() {
    return {
        provide: RXAP_PUB_SUB_DISABLE_CACHE,
        useValue: true,
    };
}
function withDisableGarbageCollector() {
    return {
        provide: RXAP_PUB_SUB_DISABLE_GARBAGE_COLLECTOR,
        useValue: true,
    };
}
function withMaxCacheSize(size) {
    return {
        provide: RXAP_PUB_SUB_DISABLE_CACHE,
        useValue: size,
    };
}
function withGarbageCollectorInterval(interval) {
    return {
        provide: RXAP_PUB_SUB_DISABLE_GARBAGE_COLLECTOR,
        useValue: interval,
    };
}

// region 
// endregion

/**
 * Generated bundle index. Do not edit.
 */

export { MessageMetaData, ProvidePubSub, PubSubService, RXAP_PUB_SUB_CACHE_SIZE, RXAP_PUB_SUB_DISABLE_CACHE, RXAP_PUB_SUB_DISABLE_GARBAGE_COLLECTOR, RXAP_PUB_SUB_GARBAGE_COLLECTOR_INTERVAL, withDisableCache, withDisableGarbageCollector, withGarbageCollectorInterval, withMaxCacheSize };
